import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { getTrades, V2Trade, V2Leg } from '../../services/optionsTraderV2Service';
import { canAccessAutoTrader } from '../../lib/entitlements';
import { RiskDisclaimer } from '../shared/RiskDisclaimer';
import ManualTradeActions from './ManualTradeActions';
import { MULTIPLIER } from './v2Shared';
import { tradeStateLabel, attentionReasonLabel, holdLine, closeReasonLabel, pnlNote } from './v2Labels';

// TT Options Trader v2 (paper) -- Trades. A copy of v1 OptionsTrades.tsx fed by
// GET /v2/trades (one request per tab; counts come from the server).

type Filter = 'all' | 'open' | 'closed';
const CLOSED = ['closed', 'expired'];

/** "SELL 5000P / BUY 4950P" -- each leg with its own P/C, puts first, then calls (IC/JL show every leg). */
export function legsLabel(legs?: V2Leg[]): string | null {
  if (!legs || legs.length === 0) return null;
  const order = (l: V2Leg) => (l.type === 'C' ? 1 : 0);
  return [...legs]
    .sort((a, b) => order(a) - order(b) || (a.side === 'short' ? -1 : 1) - (b.side === 'short' ? -1 : 1))
    .map(l => `${l.side === 'short' ? 'SELL' : 'BUY'} ${l.strike ?? '?'}${l.type || ''}`)
    .join(' / ');
}

const money0 = (n: number) => `${n >= 0 ? '+' : '-'}$${Math.abs(n).toFixed(0)}`;

const OptionsTradesV2: React.FC = () => {
  const { user, token } = useAuth();
  const [byFilter, setByFilter] = useState<Partial<Record<Filter, V2Trade[]>>>({});
  const [counts, setCounts] = useState<{ all: number; open: number; closed: number }>({ all: 0, open: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [manualOnly, setManualOnly] = useState(false);

  const enabled = canAccessAutoTrader(user, 'tastytrade');

  useEffect(() => {
    if (!token || !enabled) return;
    if (byFilter[filter]) return; // cached; one request per tab, ever
    setLoading(true);
    setFetchErr(null);
    getTrades(token, filter, 200)
      .then(res => {
        setByFilter(m => ({ ...m, [filter]: res.trades || [] }));
        if (res.counts) setCounts(res.counts);
      })
      .catch(err => setFetchErr(`Couldn't load trades: ${err?.message || 'Failed to load trades'}`))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, enabled, filter]);

  if (!enabled) {
    return (
      <div className="dashboard-container"><Navigation />
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
          <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700 }}>Options Trades v2</h1>
          <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 28, textAlign: 'center' }}>
            <p style={{ color: '#9ca3af' }}>This feature is not enabled on your account.</p>
          </div>
        </div>
      </div>
    );
  }

  // A split parent is replaced by its children, so it is hidden from every list.
  const all = (byFilter[filter] || []).filter(t => t.status !== 'split');
  const hiddenSplit = (byFilter.all || []).filter(t => t.status === 'split').length; // counts come from the API and include split parents
  const trades = manualOnly ? all.filter(t => t.source === 'manual_pickup') : all;

  const statusColor = (s: string) => {
    if (s === 'open') return '#4ade80';
    if (s === 'pending' || s === 'closing' || s === 'needs_attention') return '#fbbf24';
    if (s === 'closed' || s === 'expired') return '#6b7280';
    if (s === 'failed') return '#f87171';
    return '#6b7280';
  };
  const pnlColor = (pnl: number | null | undefined) => (pnl == null ? '#6b7280' : pnl >= 0 ? '#4ade80' : '#f87171');

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 16px', cursor: 'pointer', borderRadius: 6, fontSize: 13, fontWeight: 600,
    background: active ? '#6d28d9' : '#1f2937', color: active ? '#f9fafb' : '#6b7280',
    border: active ? '1px solid #7c3aed' : '1px solid #374151',
  });

  return (
    <div className="dashboard-container"><Navigation />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700, margin: 0 }}>Options Trades v2</h1>
            <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>Trades from your v2 engines. Each card shows whether it is paper or live.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ color: '#d1d5db', fontSize: 13 }}><input type="checkbox" aria-label="Manual engine only" checked={manualOnly} onChange={() => setManualOnly(v => !v)} /> Manual engine only</label>
            <span style={tabStyle(filter === 'all')} onClick={() => setFilter('all')}>All ({Math.max(0, counts.all - hiddenSplit)})</span>
            <span style={tabStyle(filter === 'open')} onClick={() => setFilter('open')}>Open ({counts.open})</span>
            <span style={tabStyle(filter === 'closed')} onClick={() => setFilter('closed')}>Closed ({counts.closed})</span>
          </div>
        </div>

        {fetchErr && (
          <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 6, marginBottom: 12, border: '1px solid #b91c1c', fontSize: 13 }}>{fetchErr}</div>
        )}

        {loading && <p style={{ color: '#6b7280' }}>Loading trades...</p>}

        {!loading && trades.length === 0 && (
          <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 28, textAlign: 'center' }}>
            <p style={{ color: '#9ca3af' }}>No trades found.</p>
          </div>
        )}

        {!loading && trades.map(trade => {
          const closed = CLOSED.includes(trade.status);
          const mult = MULTIPLIER[trade.symbol || ''] || 100;
          const qty = trade.quantity || 1;
          const creditDollars = trade.entryCredit != null ? trade.entryCredit * mult * qty : null;
          const maxProfit = trade.maxProfit ?? creditDollars;
          const uncapped = /naked|jade_lizard/.test(trade.strategy || '') || !(trade.legs || []).some(l => l.side === 'long');
          const maxLoss = uncapped ? null : trade.maxLoss ?? (trade.spreadWidth != null && trade.entryCredit != null && trade.strategy !== 'jade_lizard'
            ? (trade.spreadWidth - trade.entryCredit) * mult * qty : null);
          const shortLeg = (trade.legs || []).find(l => l.side === 'short');
          const delta = shortLeg?.delta;
          const dte = trade.dte;
          return (
            <div key={trade.tradeId} data-testid="v2-trade-card" style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: '16px 20px', marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 16, color: '#f9fafb' }}>{trade.symbol || '?'}</span>
                    <span style={{ color: '#fbbf24', fontSize: 13 }}>{legsLabel(trade.legs) || 'option'}</span>
                    <span style={{ color: '#6b7280', fontSize: 12 }}>exp {trade.expiration}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[
                      { label: tradeStateLabel(trade.status), color: statusColor(trade.status) },
                      { label: trade.mode === 'live' ? 'LIVE' : 'PAPER', color: trade.mode === 'live' ? '#4ade80' : '#fbbf24' },
                      ...(trade.source === 'manual_pickup' ? [{ label: trade.strategy === 'covered_call' ? 'COVERED CALL (ALERTS ONLY)' : trade.management === 'managed' ? 'MANAGED' : 'ALERTS ONLY', color: '#f472b6' }] : []),
                      ...(dte != null ? [{ label: `${dte}DTE`, color: '#a78bfa' }] : []),
                      ...(trade.engineName ? [{ label: trade.engineName, color: '#60a5fa' }] : []),
                    ].map((b, i) => (
                      <span key={i} style={{ padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: b.color + '1a', color: b.color, border: `1px solid ${b.color}33` }}>{b.label}</span>
                    ))}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {closed ? (
                    <>
                      {trade.realizedPnl != null
                        ? <div style={{ fontSize: 20, fontWeight: 700, color: pnlColor(trade.realizedPnl) }}>{money0(trade.realizedPnl)}{pnlNote(trade) && <span style={{ fontSize: 11, fontWeight: 400, color: '#fbbf24', marginLeft: 6 }}>{pnlNote(trade)}</span>}</div>
                        : <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>P&L unavailable</div>}
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{closeReasonLabel(trade.exitReason || trade.closeReason)}{trade.holdDays != null ? ` | ${trade.holdDays}d` : ''}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 20, fontWeight: 700, color: pnlColor(trade.unrealizedPnl) }}>{trade.unrealizedPnl != null ? money0(trade.unrealizedPnl) : '–'}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>unrealized</div>
                    </>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginTop: 12, fontSize: 12 }}>
                {trade.entryUnknown && <div><span style={{ color: '#6b7280' }}>Entry:</span> <span style={{ color: '#fbbf24' }}>entry unknown</span></div>}
                {trade.entryCredit != null && <div><span style={{ color: '#6b7280' }}>{trade.entryCredit >= 0 ? 'Credit:' : 'Debit:'}</span> <span style={{ color: '#4ade80' }}>{Math.abs(trade.entryCredit).toFixed(2)} (${Math.abs(creditDollars || 0).toFixed(0)})</span></div>}
                {trade.source === 'manual_pickup' && trade.exitRules && Object.keys(trade.exitRules).length > 0 && <div><span style={{ color: '#6b7280' }}>Rules:</span> <span style={{ color: '#f9fafb' }}>{Object.entries(trade.exitRules).map(([k, v]) => `${k === 'profitTargetPct' ? 'target' : k === 'stopLossMultiplier' ? 'stop' : k.replace(/([A-Z])/g, ' $1').toLowerCase()} ${v}`).join(', ')}</span></div>}
                {maxProfit != null && <div><span style={{ color: '#6b7280' }}>Max Profit:</span> <span style={{ color: '#f9fafb' }}>${maxProfit.toFixed(0)}</span></div>}
                {uncapped && <div><span style={{ color: '#6b7280' }}>Max Loss:</span> <span style={{ color: '#f87171' }}>uncapped</span></div>}
                {maxLoss != null && <div><span style={{ color: '#6b7280' }}>Max Loss:</span> <span style={{ color: '#f87171' }}>${maxLoss.toFixed(0)}</span></div>}
                {trade.spotAtEntry != null && <div><span style={{ color: '#6b7280' }}>Spot @ Entry:</span> <span style={{ color: '#f9fafb' }}>${trade.spotAtEntry.toFixed(0)}</span></div>}
                {delta != null && <div><span style={{ color: '#6b7280' }}>Delta:</span> <span style={{ color: '#f9fafb' }}>{delta.toFixed(3)}</span></div>}
                {qty > 1 && <div><span style={{ color: '#6b7280' }}>Qty:</span> <span style={{ color: '#f9fafb' }}>{qty}</span></div>}
                {trade.accountLast4 && <div><span style={{ color: '#6b7280' }}>Acct:</span> <span style={{ color: '#f9fafb' }}>***{trade.accountLast4}</span></div>}
              </div>

              {(trade.needsAttention || trade.status === 'needs_attention') && <div style={{ marginTop: 8, fontSize: 12, color: '#fbbf24' }}>{attentionReasonLabel(trade.attentionReason || 'unknown')}</div>}
              {!closed && holdLine(trade.lastExitEval) && <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>{holdLine(trade.lastExitEval)}</div>}
              {trade.source === 'manual_pickup' && token && <ManualTradeActions token={token} trade={trade} onChanged={(t, kids) => setByFilter(m => {
                // A split replaces the parent with its children; other tabs refetch on next visit.
                const cur = (m[filter] || []).map(x => (x.tradeId === t.tradeId ? { ...x, ...t } : x));
                const have = new Set(cur.map(x => x.tradeId));
                const next = [...cur, ...(kids || []).filter(k => !have.has(k.tradeId))];
                return kids && kids.length ? { [filter]: next } : { ...m, [filter]: next };
              })} />}

              <div style={{ marginTop: 6, fontSize: 11, color: '#4b5563' }}>
                {new Date(trade.createdAt).toLocaleString('en-US', { timeZone: 'America/New_York' })} ET
              </div>
            </div>
          );
        })}
        <RiskDisclaimer />
      </div>
    </div>
  );
};

export default OptionsTradesV2;
